import React, { ReactElement, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmModal } from 'slovlex-idsk-react-components';
import { Button, LoaderButton } from 'idsk-react-components';
import routes from '../../../../../../routing/routes';
import { useClient, useClientStatusContext } from '../../../../../../context/ClientStatusContext';
import { useSuccessMessageContext } from '../../../../../../context/SuccessMessageContext';
import {
  cancelVyjadrenia,
} from '../../../../../../client/admin/vyjadreniaCancelClient';
import {
  usePracovnyKrokLegislativnyMaterialContext,
} from '../../../../../../context/PracovnyKrokLegislativnyMaterialContext';
import LegislativnyMaterialItem from '../../../../../../model/pracovnyKrok/LegislativnyMaterialItem';
import splitCisloLegMaterialu from '../../../../../../function/splitCisloLegMaterialu';

interface Props {
  confirmActionQuestion?: string;
  confirmActionDescription?: string;
  reloadDetail?: () => void;
  reloadPracovneKroky?: () => void;
  goToList?: boolean;
  className?: string;
  modalClass?: string;
  legMaterial: LegislativnyMaterialItem;
  label: string;
  setIsDropdownOpened: (isDropdownOpened: boolean) => void;
}

const ModalCancelVyjadrenia = ({
  confirmActionQuestion,
  confirmActionDescription,
  reloadDetail,
  reloadPracovneKroky,
  goToList = false,
  className,
  modalClass,
  legMaterial,
  label,
  setIsDropdownOpened,
}: Props): ReactElement => {
  const navigate = useNavigate();
  const {
    clearMessages,
    displaySuccess,
  } = useSuccessMessageContext();
  const { clearStatus } = useClientStatusContext();
  const {
    setMaterial,
    setLoadingMaterial,
  } = usePracovnyKrokLegislativnyMaterialContext();

  useEffect(
    () => setMaterial(legMaterial),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [legMaterial?.uuid, legMaterial?.typ,
      legMaterial?.cisloLegislativnehoMaterialu, setMaterial],
  );

  const {
    wrapClient: vyjadreniaCancelClient,
    status: completePracovnyKrokStatus,
  } = useClient(cancelVyjadrenia);

  const {
    type,
    year,
    index,
  } = splitCisloLegMaterialu(legMaterial.cisloLegislativnehoMaterialu);

  function getRoute(): string {
    if (goToList) {
      return type === 'PI' ? routes.procesy.predbeznaInformacia.list : routes.procesy.list;
    }

    return routes.procesy.detail.withParams(type, year, index);
  }

  useEffect(() => {
    setLoadingMaterial(completePracovnyKrokStatus?.loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completePracovnyKrokStatus?.loading]);

  const onCompletePracovnyKrok = async (): Promise<void> => {
    await vyjadreniaCancelClient(type, year, index);
    navigate(getRoute());
    doReloadDetail();
    doReloadPracovneKroky();
    displaySuccess('Vaša požiadavka bola úspešne vykonaná.');
  };

  const doReloadDetail = (): void => {
    if (reloadDetail !== undefined) {
      reloadDetail();
    }
  };

  const doReloadPracovneKroky = (): void => {
    if (reloadPracovneKroky !== undefined) {
      reloadPracovneKroky();
    }
  };

  const clearStatusAndMessages = (): void => {
    clearStatus();
    clearMessages();
  };

  const renderButton = (): ReactElement => {
    if (confirmActionQuestion) {
      return (
        <ConfirmModal
          className={modalClass || ''}
          question={confirmActionQuestion}
          description={confirmActionDescription}
          cancelButtonText="Nie"
          confirmButton={
            (
              <Button
                buttonStyle="action"
                size="large"
              >
                Áno
              </Button>
            )
          }
          openButton={(
            <LoaderButton
              className={`pracovny-krok__button ${className || ''}`}
              buttonStyle="link"
              size="small"
              onClick={() => {
                clearStatusAndMessages();
                setIsDropdownOpened(false);
              }}
              loading={completePracovnyKrokStatus.loading}
              disableOutsideClickWhileLoading
            >
              {label}
            </LoaderButton>
          )}
          doAfterConfirm={onCompletePracovnyKrok}
        />
      );
    }
    return (
      <LoaderButton
        className={`pracovny-krok__button ${className || ''}`}
        buttonStyle="link"
        size="small"
        loading={completePracovnyKrokStatus.loading}
        onClick={onCompletePracovnyKrok}
        disableOutsideClickWhileLoading
      >
        {label}
      </LoaderButton>
    );
  };
  return (renderButton());
};

export default ModalCancelVyjadrenia;
